/**
 * RmsSchoolUserSetUpPassword.js
 *
 * "Set your password" for school staff: SENCOs registered by an RMS provider,
 * and class teachers a SENCO has given forms to (TDI-619).
 *
 * The invitation email links here with `?setup=true&e={base64Email}&t={token}`.
 * The page checks the link first (rmsValidateSchoolUserPasswordSetup, which
 * also says whether the account is a teacher's), then sets the password
 * through rmsCompleteSchoolUserPasswordSetup, which:
 *   - validates password strength server-side
 *   - replaces the temporary password via the Admin SDK
 *   - sets `hasSetOwnPassword=true` in custom claims and consumes the link
 *
 * After success a SENCO goes to the school sign-in page with their email
 * filled in; a teacher is signed straight in and lands on their pupils
 * (/rms/teacher). A link that is invalid, used or past its seven days offers
 * "We can email a new one", which goes through the same server-side gate as
 * "Forgotten your password?" (rmsSendSchoolUserPasswordReset).
 *
 * Built to the sign-in page designs (Figma UAXOnhR1ZvlsNl4diy00xX: Teacher /
 * Set your password 3954:256276 desktop, 3954:256468 phone), on the same
 * composition as SchoolUserSignIn.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { analytics, auth, functions } from '../../firebase';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../analytics/ga4';
import PasswordRequirements from '../../components/auth/PasswordRequirements';
import { validatePasswordString } from '../../hooks/auth/usePasswordValidation';
import HarbourBrandLockup from '../components/harbour/HarbourBrandLockup';
import HarbourButton from '../components/harbour/HarbourButton';
import { HarbourField } from '../components/harbour/HarbourField';
import HarbourNotice from '../components/harbour/HarbourNotice';
import HarbourTextLink from '../components/harbour/HarbourTextLink';
import '../components/harbour/harbour.css';
import './schoolSignIn.css';

const TEACHER_HOME = '/rms/teacher';

const COPY = {
  teacher: {
    title: 'Set your password',
    intro: "Set your password once, and you land straight into your pupils' forms.",
    submit: "Set password and open your pupils' forms",
    help: 'Need help signing in? Contact the SENCO at your school.',
  },
  school_user: {
    title: 'Set up your account',
    intro: 'Please create a secure password for your account.',
    submit: 'Set up password',
    help: null,
  },
  emailLabel: 'Your email address',
  passwordLabel: 'Choose a password',
  confirmLabel: 'Type it again',
  newLink: 'Link expired or already used? We can email a new one',
  invalidTitle: 'This link cannot be used',
};

export default function RmsSchoolUserSetUpPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [accountKind, setAccountKind] = useState('school_user');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  // A link that fails upfront validation never shows the password form.
  const [isLinkInvalid, setIsLinkInvalid] = useState(false);
  const [newLinkState, setNewLinkState] = useState('idle'); // 'idle' | 'sending' | 'sent' | 'failed'

  // ── Initialise: decode & validate the email from the URL ──────────────
  useEffect(() => {
    const initializeSetup = async () => {
      const encodedEmail = searchParams.get('e');
      const token = searchParams.get('t') || '';
      const isSetup = searchParams.get('setup') === 'true';

      if (!encodedEmail || !token || !isSetup) {
        setError('Invalid setup link. Please contact the referral service.');
        setIsLinkInvalid(true);
        logEvent(analytics, 'rms_school_user_setup_invalid_link', { error: 'missing_params' });
        setIsInitializing(false);
        return;
      }

      try {
        const decoded = atob(encodedEmail);
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(decoded)) throw new Error('Invalid email format');
        setEmail(decoded);
        setSetupToken(token);

        // Upfront server-side check: an invalid, revoked, expired or
        // already-consumed link fails here, before the user types anything.
        try {
          const validateSetup = httpsCallable(functions, 'rmsValidateSchoolUserPasswordSetup');
          const res = await validateSetup({ email: decoded, token });
          if (res?.data?.accountKind === 'teacher') setAccountKind('teacher');
        } catch (validationError) {
          const code = validationError?.code || '';
          const msg = validationError?.message || '';
          if (code === 'functions/failed-precondition' && msg.toLowerCase().includes('already')) {
            logEvent(analytics, 'rms_school_user_setup_already_complete', {});
            navigate('/rms/school-sign-in?message=account_already_setup');
            return;
          }
          if (code === 'functions/not-found' || code === 'functions/permission-denied' || code === 'functions/failed-precondition') {
            setError('This setup link is not valid. Please contact the referral service.');
            setIsLinkInvalid(true);
            logEvent(analytics, 'rms_school_user_setup_invalid_link', { error: 'rejected_by_server' });
            return;
          }
          // Transient errors (network, internal) fall through and let the
          // user attempt the form; the submit enforces the same gates.
        }
      } catch (err) {
        setError('Invalid setup link. Please contact the referral service.');
        setIsLinkInvalid(true);
        logEvent(analytics, 'rms_school_user_setup_invalid_link', {
          error_type: err.name,
        });
      } finally {
        setIsInitializing(false);
      }
    };

    initializeSetup();
  }, [searchParams, navigate]);

  // "We can email a new one": the same gate as "Forgotten your password?".
  // An account that has not set its password yet gets its set-up email again
  // with a fresh link; any other address gets nothing, with the same answer.
  const sendNewLink = async () => {
    if (!email || newLinkState === 'sending') return;
    setNewLinkState('sending');
    try {
      const sendReset = httpsCallable(functions, 'rmsSendSchoolUserPasswordReset');
      await sendReset({ email });
      setNewLinkState('sent');
      logEvent(analytics, 'rms_school_user_setup_new_link_requested', {});
    } catch (_) {
      setNewLinkState('failed');
    }
  };

  // ── Submit handler ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const { errors: validationErrors } = validatePasswordString(newPassword);
    const nextFieldErrors = {};
    if (validationErrors.length > 0) nextFieldErrors.newPassword = validationErrors[0];
    if (newPassword !== confirmPassword) nextFieldErrors.confirmPassword = 'Passwords do not match';
    setFieldErrors(nextFieldErrors);
    if (Object.keys(nextFieldErrors).length) return;

    setIsLoading(true);
    try {
      const fn = httpsCallable(functions, 'rmsCompleteSchoolUserPasswordSetup');
      const res = await fn({ email, token: setupToken, newPassword });
      const kind = res?.data?.accountKind || accountKind;

      logEvent(analytics, 'rms_school_user_password_setup_success', {});

      const encoded = btoa(email);
      if (kind === 'teacher') {
        // "Set your password once, and you land straight into your pupils'
        // forms": sign the teacher in with the password they just chose.
        try {
          await signInWithEmailAndPassword(auth, email, newPassword);
          navigate(TEACHER_HOME);
        } catch (_) {
          navigate(`/rms/school-sign-in?message=setup_complete&e=${encodeURIComponent(encoded)}&continue=${encodeURIComponent(TEACHER_HOME)}`);
        }
        return;
      }
      // A SENCO signs in on the school sign-in page, email filled in.
      navigate(`/rms/school-sign-in?message=setup_complete&e=${encodeURIComponent(encoded)}`);
    } catch (err) {
      let message = 'An error occurred. Please try again.';

      if (err.code === 'functions/invalid-argument') {
        message = err.message?.includes('Password must')
          ? err.message
          : 'Please check your password requirements and try again.';
      } else if (err.code === 'functions/not-found') {
        message = 'This setup link is not valid. Please contact the referral service.';
      } else if (err.code === 'functions/permission-denied') {
        message = 'Invalid account type for this setup process.';
      } else if (err.code === 'functions/failed-precondition') {
        if (err.message?.includes('Account already set up')) {
          message = 'Account already set up. Please sign in with your password.';
          setTimeout(
            () => navigate('/rms/school-sign-in?message=account_already_setup'),
            2000,
          );
        } else if (err.message?.includes('Email verification required')) {
          message = 'Email verification required. Please contact the referral service.';
        } else {
          message = err.message;
        }
      } else if (err.code === 'functions/internal') {
        message = 'A technical error occurred. Please try again or contact the referral service.';
      } else if (err.message?.includes('Password must')) {
        message = err.message;
      }

      setError(message);
      logEvent(analytics, 'rms_school_user_password_setup_error', {
        error_code: err.code,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copy = COPY[accountKind] || COPY.school_user;

  const newLinkControl = (
    <div className="flex flex-col gap-3">
      {newLinkState === 'sent' ? (
        <HarbourNotice kind="success" title={`A new link is on its way to ${email}.`} />
      ) : (
        <HarbourTextLink
          className="school-signin-reset"
          onClick={sendNewLink}
          disabled={newLinkState === 'sending' || !email}
        >
          {COPY.newLink}
        </HarbourTextLink>
      )}
      {newLinkState === 'failed' && (
        <HarbourNotice kind="error" title="That did not work">
          A new link could not be sent. Check your connection and try again.
        </HarbourNotice>
      )}
    </div>
  );

  return (
    <div className="harbour harbour-page school-signin">
      <header className="school-signin-top-bar">
        <HarbourBrandLockup />
      </header>
      <main className="school-signin-main">
        <div className="school-signin-card">
          {isInitializing ? (
            <div aria-busy="true" className="flex flex-col gap-4">
              <h1 className="school-signin-title">Checking your link...</h1>
              <div className="harbour-skeleton h-[48px] animate-pulse" aria-hidden="true" />
              <div className="harbour-skeleton h-[48px] animate-pulse" aria-hidden="true" />
            </div>
          ) : isLinkInvalid ? (
            <>
              <h1 className="school-signin-title">{COPY.invalidTitle}</h1>
              <HarbourNotice kind="error" title={error} />
              {email ? (
                newLinkControl
              ) : (
                <p className="harbour-body-16">
                  If you were expecting to set up an account, please ask the referral
                  service to send you a new link.
                </p>
              )}
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <h1 className="school-signin-title">{copy.title}</h1>
                <p className="harbour-body-16">{copy.intro}</p>
              </div>

              {!!error && <HarbourNotice kind="error" title={error} />}

              <form className="school-signin-form" onSubmit={handleSubmit} noValidate>
                {/* Read-only: the address the link was sent to, as plain text. */}
                <div className="harbour-field school-signin-field">
                  <span className="harbour-field__label">{COPY.emailLabel}</span>
                  <p className="harbour-body-16" data-testid="setup-email">{email}</p>
                </div>
                <div className="school-signin-field">
                  <HarbourField
                    label={COPY.passwordLabel}
                    name="new-password"
                    reveal
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isLoading}
                    autoComplete="new-password"
                    error={fieldErrors.newPassword}
                  />
                  <PasswordRequirements value={newPassword} className="mt-2" />
                </div>
                <HarbourField
                  className="school-signin-field"
                  label={COPY.confirmLabel}
                  name="confirm-password"
                  reveal
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="new-password"
                  error={fieldErrors.confirmPassword}
                />
                <div className="school-signin-actions">
                  <HarbourButton
                    kind="primary"
                    type="submit"
                    busy={isLoading}
                    busyLabel="Setting up your account..."
                    disabled={isLoading}
                    aria-busy={isLoading || undefined}
                  >
                    {copy.submit}
                  </HarbourButton>
                </div>
                {newLinkControl}
              </form>
            </>
          )}
        </div>

        {copy.help && !isInitializing && (
          <aside className="school-signin-help">
            <HarbourNotice kind="info">{copy.help}</HarbourNotice>
          </aside>
        )}

        <nav className="school-signin-footer-links" aria-label="Portal links">
          <a href="#help">Help</a>
          <span aria-hidden="true"> · </span>
          <a href="#privacy">Privacy</a>
          <span aria-hidden="true"> · </span>
          <a href="#accessibility">Accessibility</a>
          <span aria-hidden="true"> · </span>
          <a href="#feedback">Feedback</a>
        </nav>
      </main>
    </div>
  );
}
